import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["entity", "entities", "entityFields", "itemsToDisplay", "children", "VariantComponent"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { MainContext } from '@blaze-cms/nextjs-components';
import { generateSingleItemQuery } from '../../application/query';
import { SEARCH_PUBLISHED_CONTENT, AND, FULL } from '../../constants';
import { useGetEntitySchemasAsObj, useGetImages } from '../../hooks';
import { getUpdatedFilterBy, getEntityData, getRequiredSchemas, sortResponseData, getUnpublishedEntityName, renderChildren, splitChildren, removeExtraItems, appendImages } from '../../helpers';
import { filtersSetup, filterQuerySetup, shouldSkipSingleQuery, shouldReturn, getUpdatedSortProperties, getUpdatedItemsToDisplay } from './helpers';
const CardsContainer = dynamic(() => import('./CardsContainer'));
const CardsRender = _ref => {
  let {
      entity: legacyEntity,
      entities,
      entityFields,
      itemsToDisplay,
      children,
      VariantComponent
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const {
    isPreview
  } = useContext(MainContext);
  const {
    parent: {
      itemId: parentId,
      itemEntity: parentEntity
    },
    filterBy = [],
    filterByProperty = [],
    sortby,
    sort,
    sortProperties = [],
    operator: filterOperator = AND,
    useRandomSort
  } = otherProps;
  const [updatedSortProperties, sortbyFilters] = getUpdatedSortProperties(sort, sortby, sortProperties);
  const variableProps = _objectSpread(_objectSpread({}, otherProps), {}, {
    sortProperties: updatedSortProperties,
    sort: null,
    sortby: null
  });
  const entitiesToUse = entities || [legacyEntity];
  const primaryEntityToUse = entitiesToUse[0];
  const unpublishedParentEntityName = getUnpublishedEntityName(parentEntity);
  const allEntityNames = [...entitiesToUse, unpublishedParentEntityName];
  const uniqueEntityNames = [...new Set(allEntityNames)];
  const {
    data: mainSchemas = {},
    loading: _loading,
    error: _error
  } = useGetEntitySchemasAsObj(uniqueEntityNames);
  const parentSchema = mainSchemas[unpublishedParentEntityName] || {};
  const filterEntitySchema = mainSchemas[primaryEntityToUse] || {};
  const entitiesTypes = entitiesToUse.map(ent => getEntityData(ent, mainSchemas[ent]));
  const graphqlEntityMap = entitiesTypes.reduce((acc, entityType) => _objectSpread(_objectSpread({}, acc), {}, {
    [entityType.entityType]: entityType.entity
  }), {});
  const docTypes = entitiesTypes.map(({
    docType: type
  }) => type);
  const entityTypes = entitiesTypes.map(({
    entityType: eType
  }) => eType);
  const {
    style
  } = otherProps;
  const {
    getAction,
    queryProps,
    inheritedFilters,
    entitySameAsCurrentItemEntity,
    action,
    filterEntityRelations
  } = filtersSetup({
    entityFields,
    entityTypes,
    filterBy,
    filterByProperty,
    entities: entitiesToUse,
    currentSchema: parentSchema,
    filterEntitySchema,
    isPreview,
    itemEntityUpdated: unpublishedParentEntityName,
    isFull: style === FULL
  });
  const {
    data: {
      entityData: parentEntityData = {}
    } = {},
    error: _err,
    loading: _load
  } = useQuery(generateSingleItemQuery(getAction, queryProps), {
    variables: {
      id: parentId
    },
    skip: shouldSkipSingleQuery(_loading, getAction, queryProps)
  });
  const {
    data: requiredSchema = {},
    loading: schemaLoading,
    error: schemaError
  } = useGetEntitySchemasAsObj(getRequiredSchemas([...filterByProperty, ...sortbyFilters], filterEntitySchema), _loading);
  const updatedFilterBy = getUpdatedFilterBy(inheritedFilters, parentEntityData, parentSchema, filterEntitySchema, parentId, variableProps);
  const updatedItemsToDisplay = getUpdatedItemsToDisplay({
    itemsToDisplay,
    inheritedFilters,
    parentSchema,
    parentEntityData
  });
  const {
    limit,
    variables
  } = filterQuerySetup({
    getEntitySchemas: Object.values(requiredSchema),
    entity: primaryEntityToUse,
    updatedFilterBy,
    variableProps,
    docType: docTypes,
    filterOperator,
    filterEntityRelations,
    itemsToDisplay: updatedItemsToDisplay,
    entitySameAsCurrentItemEntity,
    useRandomSort
  });
  const hasNoFiltersOrItemsToDisplay = !updatedItemsToDisplay.length && !updatedFilterBy;
  const {
    data: cardData = {},
    error: cardsError
  } = useQuery(action, {
    variables,
    fetchPolicy: useRandomSort ? 'cache-and-network' : 'cache-first',
    skip: shouldReturn(hasNoFiltersOrItemsToDisplay, _loading, _load, schemaLoading, _error, _err, schemaError)
  });
  const imageIds = updatedItemsToDisplay.map(({
    imageId
  }) => imageId).filter(Boolean);
  const {
    data: imagesData,
    loading: imagesLoading
  } = useGetImages(imageIds, true);
  const isLoading = shouldReturn(_loading, _load, schemaLoading, imagesLoading);
  const hasError = shouldReturn(_error, _err, schemaError);
  if (cardsError) return cardsError.message;
  if (hasNoFiltersOrItemsToDisplay || isLoading || hasError) {
    return null;
  }
  let {
    results: cardDataResults
  } = cardData[SEARCH_PUBLISHED_CONTENT] || {};
  if (!cardDataResults || !cardDataResults.length) return null;
  cardDataResults = entitySameAsCurrentItemEntity ? removeExtraItems(cardDataResults, limit, parentId) : cardDataResults;
  if (!cardDataResults.length) return null;
  const orderedData = useRandomSort || updatedSortProperties.length ? cardDataResults : sortResponseData(cardDataResults, updatedItemsToDisplay);
  const {
    regularChildren,
    gtmChildren
  } = splitChildren(children, cardDataResults, graphqlEntityMap, {
    name: variableProps.name
  });
  const updatedCards = appendImages(imagesData, orderedData, updatedItemsToDisplay);
  const displayedCards = updatedCards.slice(0, limit);
  return /*#__PURE__*/React.createElement(React.Fragment, null, gtmChildren && renderChildren(gtmChildren), /*#__PURE__*/React.createElement(CardsContainer, _extends({
    cardData: displayedCards,
    entity: primaryEntityToUse,
    gtmChildren: gtmChildren,
    cardChildren: regularChildren,
    graphqlEntityMap: graphqlEntityMap,
    VariantComponent: VariantComponent
  }, variableProps)));
};
CardsRender.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  entity: PropTypes.string.isRequired,
  entityFields: PropTypes.string.isRequired,
  itemsToDisplay: PropTypes.array,
  entities: PropTypes.array,
  VariantComponent: PropTypes.func
};
CardsRender.defaultProps = {
  children: [],
  itemsToDisplay: [],
  entities: [],
  VariantComponent: null
};
export default CardsRender;
//# sourceMappingURL=CardsRender.js.map